import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Eye, ArrowRight, Heart, Phone, Mail, Sparkles } from 'lucide-react';
import Hero from '../components/Hero';
import DestinationCard from '../components/DestinationCard';
import AboutSection from '../components/AboutSection';
import { destinations } from '../data';
import SEO from '../components/SEO';
import { useLanguage } from '../components/LanguageContext';
import { buildOrganizationSchema } from '../lib/schema';

function Home() {
  const { language, t, translateObject, getLocalizedPath } = useLanguage();
  const translatedDestinations = destinations.map(d => translateObject(d));

  const homeSchema = buildOrganizationSchema({ language, asTravelAgency: true });

  return (
    <>
      <SEO 
        title={language === 'da' ? "Handicaprejser i Europa | Indhent et uforpligtende tilbud" : "Accessible Holidays in Europe | Request a Free Quote"} 
        description={language === 'da' ? "Find handicapvenlige rejser og hoteller, der fungerer i praksis. Få personlig rådgivning og et uforpligtende tilbud på din næste ferie." : "Find wheelchair friendly holidays and hotels that work in practice. Get personal advice and a free quote for your next trip."}
        ogImage="https://assets.auxodev.dk/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/ceef4750-2ca2-43c1-b82b-e06ba062b589.jpeg"
        schemaMarkup={homeSchema}
      />
      <Hero />

      {/* Trust Banner below Hero Image */}
      <div className="bg-[#99A18C]/10 border-y border-[#99A18C]/20 py-4 px-6 text-center">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-[#5A634E] text-sm md:text-base font-medium">
          <ShieldCheck className="w-5 h-5 text-[#99A18C] shrink-0" />
          <span>
            {language === 'da' 
              ? 'Vi har personligt besøgt eller grundigt kvalitetssikret alle hoteller og rejsemål' 
              : 'We have personally visited or thoroughly quality-assured all hotels and destinations'}
          </span>
        </div>
      </div>

      {/* Destinations Section */}
      <section id="destinationer" className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-16"
          >
            <span className="text-[#99A18C] font-bold uppercase tracking-widest text-xs mb-4 block">
              {language === 'da' ? 'Gennemtestede Rejsemål' : 'Personally Tested Destinations'}
            </span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#99A18C] italic">
              {language === 'da' ? 'Udforsk destinationer til handicapvenlige rejser' : 'Explore Accessible Holiday Destinations'}
            </h2>
            <p className="mt-6 text-stone-600 max-w-7xl mx-auto text-lg leading-relaxed">
              {language === 'da' ? (
                "Alora Rejser er skabt ud fra mere end 10 års egne erfaringer med at rejse med kørestol. Jeg ved, hvor stor forskel de små detaljer kan gøre – og hvor svært det kan være at gennemskue, om et hotel, et badeværelse, en pool eller området omkring hotellet faktisk fungerer, når man først står der."
              ) : (
                "Alora Rejser was created from more than 10 years of personal experience traveling in a wheelchair. I know how much difference the small details can make – and how hard it can be to figure out whether a hotel, bathroom, pool, or area around the hotel actually works when you're there."
              )}
            </p>
            <p className="mt-4 text-stone-800 font-bold max-w-7xl mx-auto text-lg leading-relaxed">
              {language === 'da' ? (
                "Mit mål er, at du skal kunne glæde dig til ferien – uden at skulle være den, der undersøger hver eneste detalje først."
              ) : (
                "My goal is for you to be able to look forward to your holiday – without having to be the one inspecting every detail first."
              )}
            </p>
            <p className="mt-4 text-stone-600 italic max-w-7xl mx-auto text-lg leading-relaxed">
              {language === 'da' ? (
                "For en ferie skal ikke bare se tilgængelig ud på papiret. Den skal fungere i virkeligheden, for alle parter🙏🏼❤️"
              ) : (
                "Because a holiday shouldn't just look accessible on paper. It has to work in reality, for everyone🙏🏼❤️"
              )}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {translatedDestinations.slice(0, 3).map((dest, index) => (
              <DestinationCard key={dest.id} destination={dest} index={index} />
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="mt-16 text-center"
          >
            <Link 
              to={getLocalizedPath('destinations', language)} 
              className="inline-flex items-center gap-2 bg-[#99A18C] text-white px-10 py-5 rounded-full font-bold hover:bg-[#8a917e] transition-all transform hover:-translate-y-1 shadow-xl"
            >
              {language === 'da' ? 'Se alle vores rejsemål' : 'Explore All Destinations'}
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Transparency / Why Us Section */}
      <section id="priser" className="bg-white py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <span className="text-[#99A18C] font-bold uppercase tracking-widest text-xs mb-4 block">
                  {language === 'da' ? 'Vores værdier' : 'Our Values'}
                </span>
                <h2 className="text-3xl md:text-5xl font-serif font-bold text-slate-800 mb-8 leading-tight italic">
                  {language === 'da' ? (
                    <>Handicaprejser med <br />kvalitet i alle led</>
                  ) : (
                    <>Accessible Holidays <br />with Quality Throughout</>
                  )}
                </h2>
              </motion.div>
              
              <div className="space-y-10">
                <motion.div 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="flex gap-6"
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-[#99A18C]/10 rounded-2xl flex items-center justify-center text-[#99A18C]">
                    <ShieldCheck size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {language === 'da' ? 'Ingen skjulte gebyrer' : 'No Hidden Fees'}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {language === 'da' ? (
                        "Vi oplyser prisen inklusive de væsentlige elementer. Vi inkluderer altid nødvendig assistance og tilgængelige rammer, så du ved, hvad du betaler for."
                      ) : (
                        "We present the price including all essential elements. We always include necessary assistance and accessible facilities, so you know exactly what you are paying for."
                      )}
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex gap-6"
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-[#99A18C]/10 rounded-2xl flex items-center justify-center text-[#99A18C]">
                    <Eye size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {language === 'da' ? 'Tryghedspakke' : 'Peace of Mind Package'}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {language === 'da' ? (
                        "En tryghedspakke, hvor du modtager information om tilgængelighed på hotellet, i nærområdet, strandforhold, handicaptoiletter, transportmuligheder, apoteker og akutnumre."
                      ) : (
                        "A safety package where you receive detailed information about accessibility at the hotel, local area, beach conditions, accessible toilets, transport options, pharmacies, and emergency numbers."
                      )}
                    </p>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex gap-6"
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-[#99A18C]/10 rounded-2xl flex items-center justify-center text-[#99A18C]">
                    <Heart size={28} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">
                      {language === 'da' ? 'Reel erfaring' : 'Real Experience'}
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-6">
                      {language === 'da' ? (
                        <>
                          Vores rejser er udvalgt på baggrund af reel erfaring, og vi har selv besøgt mange af destinationerne og de <Link to={getLocalizedPath('hotels', language)} className="text-[#99A18C] underline hover:text-[#828a75] transition-colors">handicapvenlige hoteller</Link> for at sikre, at de fungerer i praksis.
                        </>
                      ) : (
                        <>
                          Our holidays are selected based on real personal experience. We have personally visited many of the destinations and <Link to={getLocalizedPath('hotels', language)} className="text-[#99A18C] underline hover:text-[#828a75] transition-colors">wheelchair friendly hotels</Link> to ensure they work in practice.
                        </>
                      )}
                    </p>
                    <Link to={getLocalizedPath('prices', language)} className="text-[#99A18C] font-bold flex items-center gap-2 hover:gap-3 transition-all">
                      {language === 'da' ? 'Læs mere om vores priser og værdier' : 'Learn more about our prices and values'}
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>

            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              whileInView={{ scale: 1, opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="bg-[#99A18C] -mx-6 md:mx-0 rounded-none md:rounded-[3rem] p-12 md:p-16 text-white relative shadow-2xl"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
              <h3 className="text-3xl font-serif font-bold mb-6 italic">
                {language === 'da' ? '"Vi gør det uoverskuelige overskueligt."' : '"We make the overwhelming manageable."'}
              </h3>
              <p className="text-white text-lg mb-10 leading-relaxed">
                {language === 'da' ? (
                  "Hos Alora Rejser fjerner vi det tunge arbejde med at undersøge tilgængelighed. Vi kender destinationerne, så du kan fokusere på oplevelsen frem for bekymringerne."
                ) : (
                  "At Alora Rejser, we remove the heavy lifting of researching accessibility. We know the destinations inside out, so you can focus on the experience rather than the worries."
                )}
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-t border-white/20 pt-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/50 shadow-lg shrink-0">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=85,format=auto/assets/1a80aa53-8838-4db9-a7e2-061c5ee04d6a/alora-rejser-om-os.png" 
                      alt="Alora Rejser" 
                      className="w-full h-full object-cover object-top" 
                    />
                  </div>
                  <div>
                    <p className="font-bold">Alora Rejser</p>
                    <p className="text-white/80 text-sm italic">
                      {language === 'da' ? 'Hjertet bag bureauet' : 'The heart behind the agency'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <a 
                    href="tel:+4540500780" 
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg border border-white/10 transition-all font-medium text-sm text-white group"
                  >
                    <Phone size={14} className="text-white/80 group-hover:scale-110 transition-transform" />
                    <span>+45 40 50 07 80</span>
                  </a>
                  <a 
                    href="mailto:info@alorarejser.dk" 
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg border border-white/10 transition-all font-medium text-sm text-white group"
                  >
                    <Mail size={14} className="text-white/80 group-hover:scale-110 transition-transform" />
                    <span>info@alorarejser.dk</span>
                  </a>
                </div>
              </div>
              <div className="mt-12 p-6 bg-white/10 rounded-2xl border border-white/10">
                <p className="text-xs text-white uppercase font-bold tracking-widest mb-2">
                  {language === 'da' ? 'Prisoplysning' : 'Pricing Information'}
                </p>
                <p className="text-sm italic leading-relaxed">
                  {language === 'da' ? (
                    "Prisen er en fra-pris og afhænger blandt andet af afrejsedato, rejseperiode, værelsestype, ledige pladser samt eventuelle særlige behov. Kontakt os for et konkret og uforpligtende tilbud på din rejse."
                  ) : (
                    "The price is a starting price and depends on departure date, travel period, room type, availability, and any special needs. Contact us for a specific, non-binding quote for your trip."
                  )}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <AboutSection />
    </>
  );
}

export default Home;
